import React from 'react';
import {
  Box,
  Typography,
  Tooltip,
  IconButton,
  Switch,
  FormControlLabel,
} from '@mui/material';
import SettingsIcon from '@mui/icons-material/Settings';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import DeleteIcon from '@mui/icons-material/Delete';
import AddIcon from '@mui/icons-material/Add';
import { CustomAutocomplete } from '../../../Infra/CustomAutocomplete/CustomAutocomplete';
import { useConfigureMappingStep } from './useConfigureMappingStep';
import { StepMetadata } from '../../../../pages/DataFeedWizard/types';
import styles from './ConfigureMappingStep.module.scss';
import { CustomButton } from '../../../Infra/CustomButton/CustomButton';
import { VALIDATION_MESSAGES } from './constants';

export const ConfigureMappingStep: React.FC = () => {
  const {
    columnOptions,
    configureMappingData,
    handleAccountNumberColumnChange,
    handleIsinColumnChange,
    handleSymbolColumnChange,
    handleAddOTCColumn,
    handleRemoveOTCColumn,
    handleOTCColumnChange,
    handleOTCColumnIdentifierChange,
    getAvailableOTCColumns,
    availableIsinColumns,
    availableSymbolColumns,
    validationErrors,
    isOTCSectionEnabled,
    isExcelUpload,
  } = useConfigureMappingStep();

  return (
    <Box className={styles.container}>
      {/* Account Number Mapping Section */}
      <Box className={styles.section}>
        <Typography className={styles.sectionTitle}>
          Account Number Mapping
        </Typography>

        <Box className={styles.fieldGroup}>
          <Box className={styles.labelRow}>
            <Typography className={styles.label}>
              Map the column containing account numbers
            </Typography>
            <Tooltip title="Required for linking transactions to custodian accounts">
              <HelpOutlineIcon className={styles.helpIcon} />
            </Tooltip>
          </Box>

          <Typography variant="caption" className={styles.fieldLabel}>
            Account Number Source Column
          </Typography>

          <CustomAutocomplete
            data-testid="account-number-column-selector"
            value={
              configureMappingData.accountNumberColumn
                ? columnOptions.find(
                    opt =>
                      opt.value === configureMappingData.accountNumberColumn
                  ) || null
                : null
            }
            onChange={(_, value) =>
              handleAccountNumberColumnChange(value?.value || null)
            }
            options={columnOptions}
            getOptionLabel={option => option.label}
            noOptionsText="No columns available"
            placeholder="Select column"
            error={validationErrors?.accountNumberColumn}
            helperText={
              validationErrors?.accountNumberColumn
                ? VALIDATION_MESSAGES.ACCOUNT_NUMBER_REQUIRED
                : ''
            }
            className={styles.autocompleteField}
          />
        </Box>
      </Box>

      {/* ISIN/Symbol Search Configuration Section */}
      <Box className={styles.section}>
        <Typography className={styles.sectionTitle}>
          ISIN/Symbol Search Configuration
        </Typography>

        <Typography className={styles.sectionDescription}>
          Map columns containing security identifiers for instrument matching
        </Typography>

        <Box className={styles.fieldGroup}>
          <Typography variant="caption" className={styles.fieldLabel}>
            ISIN Source Column
          </Typography>
          <CustomAutocomplete
            data-testid="isin-column-selector"
            value={
              configureMappingData.isinColumn
                ? availableIsinColumns.find(
                    opt => opt.value === configureMappingData.isinColumn
                  ) ||
                  columnOptions.find(
                    opt => opt.value === configureMappingData.isinColumn
                  ) ||
                  null
                : null
            }
            onChange={(_, value) =>
              handleIsinColumnChange(value?.value || null)
            }
            options={availableIsinColumns}
            getOptionLabel={option => option.label}
            noOptionsText="No columns available"
            placeholder="Select column"
            error={validationErrors?.isinColumn}
            helperText={
              validationErrors?.isinColumn
                ? VALIDATION_MESSAGES.ISIN_REQUIRED
                : ''
            }
            className={styles.autocompleteField}
          />
        </Box>

        <Box className={styles.fieldGroup}>
          <Typography variant="caption" className={styles.fieldLabel}>
            Symbol Source Column
          </Typography>
          <CustomAutocomplete
            data-testid="symbol-column-selector"
            value={
              configureMappingData.symbolColumn
                ? availableSymbolColumns.find(
                    opt => opt.value === configureMappingData.symbolColumn
                  ) ||
                  columnOptions.find(
                    opt => opt.value === configureMappingData.symbolColumn
                  ) ||
                  null
                : null
            }
            onChange={(_, value) =>
              handleSymbolColumnChange(value?.value || null)
            }
            options={availableSymbolColumns}
            getOptionLabel={option => option.label}
            noOptionsText="No columns available"
            placeholder="Select column"
            error={validationErrors?.symbolColumn}
            helperText={
              validationErrors?.symbolColumn
                ? VALIDATION_MESSAGES.SYMBOL_REQUIRED
                : ''
            }
            className={styles.autocompleteField}
          />
        </Box>
      </Box>

      {/* OTC Mapping Section */}
      <Box className={styles.section}>
        <Typography className={styles.sectionTitle}>OTC Mapping</Typography>

        {!isOTCSectionEnabled && (
          <Typography className={styles.disabledMessage}>
            Please select ISIN and Symbol columns first to enable OTC mapping
          </Typography>
        )}

        <Box
          className={isOTCSectionEnabled ? undefined : styles.disabledSection}
        >
          <Typography className={styles.sectionDescription}>
            Select additional columns to display for unmatched securities
          </Typography>

          {configureMappingData.otcColumns.map((otcColumn, index) => {
            const isFirstColumn = index === 0;
            const availableColumns = getAvailableOTCColumns(index);

            return (
              <Box key={index} className={styles.fieldGroup}>
                <Typography variant="caption" className={styles.fieldLabel}>
                  OTC Column {index + 1}
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
                  <CustomAutocomplete
                    value={
                      availableColumns.find(
                        opt => opt.value === otcColumn.column
                      ) || null
                    }
                    onChange={(_, value) =>
                      handleOTCColumnChange(index, value?.value || '')
                    }
                    options={availableColumns}
                    getOptionLabel={option => option.label}
                    noOptionsText="No columns available"
                    placeholder="Select column"
                    className={styles.autocompleteField}
                    sx={{ flex: 1 }}
                  />
                  {isExcelUpload && (
                    <Tooltip title="Mark rows with this column as OTC when security is not found">
                      <FormControlLabel
                        control={
                          <Switch
                            checked={otcColumn.isIdentifier ?? false}
                            onChange={e =>
                              handleOTCColumnIdentifierChange(
                                index,
                                e.target.checked
                              )
                            }
                            disabled={!otcColumn.column}
                            size="small"
                          />
                        }
                        label="Identifier"
                        className={styles.identifierToggle}
                      />
                    </Tooltip>
                  )}
                  {!isFirstColumn && (
                    <IconButton
                      onClick={() => handleRemoveOTCColumn(index)}
                      size="small"
                      color="error"
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  )}
                </Box>
              </Box>
            );
          })}

          <Box sx={{ display: 'flex', justifyContent: 'flex-start' }}>
            <CustomButton
              startIcon={<AddIcon />}
              onClick={handleAddOTCColumn}
              variant="outlined"
              size="small"
              className={styles.addColumnButton}
            >
              Add Column
            </CustomButton>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export const stepMetadata: StepMetadata = {
  title: 'Field Mapping Configuration',
  subtitle: 'Configure how your uploaded file columns map to Illio fields',
  icon: <SettingsIcon />,
};
